import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle, KeyRound, Link2 } from "lucide-react";
import api from "../../lib/api";
import KolMyGlassLogo from "../../components/KolMyGlassLogo";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [magicEmail, setMagicEmail] = useState("");
  const [magicStatus, setMagicStatus] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");
  const [magicMessage, setMagicMessage] = useState("");

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }
    try {
      setLoading(true);
      setError("");
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSuccess(true);
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : null;
      setError(msg || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!magicEmail.trim() || !magicEmail.includes("@")) {
      setMagicStatus("error");
      setMagicMessage("Enter a valid email");
      return;
    }
    setMagicStatus("sending");
    setMagicMessage("");
    try {
      const { data } = await api.post("/auth/magic-link", {
        email: magicEmail.trim(),
      });
      setMagicStatus("sent");
      setMagicMessage(data?.message || "Check your email for the link.");
    } catch (err: unknown) {
      setMagicStatus("error");
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : null;
      setMagicMessage(msg || "Could not send sign-in link");
    }
  };

  if (success) {
    return (
      <div className="theme-agency relative min-h-screen w-full overflow-x-hidden px-4 py-10 text-glass-text login-glass-bg">
        <div className="relative z-10 mx-auto flex w-full max-w-lg flex-col items-center gap-8 pb-16">
          <Link
            to="/"
            className="outline-none focus-visible:rounded-lg focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-rgb)/0.55)] focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-900"
          >
            <KolMyGlassLogo variant="hero" heroScale={0.6} />
          </Link>
          <div className="glass-card w-full max-w-md p-8 text-center shadow-xl animate-fade-in">
            <CheckCircle className="mx-auto mb-4 h-16 w-16 text-emerald-500 dark:text-emerald-400" />
            <h2 className="text-2xl font-bold tracking-tight text-glass-text">
              Check your email
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-glass-text-2">
              If an account exists for that address, we sent a link to reset
              your password. Open it and set a new password on the secure page.
            </p>
            <Link
              to="/login"
              className="mt-8 inline-flex w-full items-center justify-center rounded-xl border border-glass bg-glass-2 px-4 py-3 text-sm font-medium text-glass-text transition-colors hover:bg-glass-3"
            >
              Back to sign in
            </Link>
          </div>
          <footer className="text-center text-sm text-glass-text-3">
            © {new Date().getFullYear()} QueueMart 1260221-X All rights
            reserved. ·{" "}
            <Link to="/" className="text-accent2 hover:underline">
              Home
            </Link>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-agency relative min-h-screen w-full overflow-x-hidden px-4 py-10 text-glass-text login-glass-bg">
      <div className="relative z-10 mx-auto flex w-full max-w-lg flex-col items-stretch gap-8 pb-16">
        <Link
          to="/"
          className="self-center outline-none focus-visible:rounded-lg focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-rgb)/0.55)] focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-900"
        >
          <KolMyGlassLogo variant="hero" heroScale={0.6} />
        </Link>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-medium text-glass-text-2 transition-colors hover:text-glass-text"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to sign in
        </Link>

        <div className="space-y-6 animate-fade-in">
          <header>
            <h1 className="text-2xl font-bold tracking-tight text-glass-text">
              Account access
            </h1>
            <p className="mt-2 text-sm text-glass-text-2 leading-relaxed">
              Reset your password or request a one-time email link to sign in
              without a password.
            </p>
          </header>

          <div className="glass-card p-6 shadow-xl">
            <div className="flex items-center gap-2 text-glass-text">
              <KeyRound className="h-5 w-5 text-accent2" />
              <h2 className="text-lg font-semibold">Reset password</h2>
            </div>
            <p className="mt-2 text-xs text-glass-text-2 leading-relaxed">
              We&apos;ll email a reset link if an account exists. For security,
              we won&apos;t confirm whether this email is registered.
            </p>
            <form onSubmit={handleRequestReset} className="mt-5 space-y-4">
              <div>
                <label
                  htmlFor="forgot-email"
                  className="mb-1 block text-xs font-medium uppercase tracking-wide text-glass-text-3"
                >
                  Email
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-glass-text-3" />
                  <input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="glass-input w-full py-2.5 pl-10 pr-3 text-sm"
                  />
                </div>
              </div>
              {error ? (
                <p className="text-sm text-rose-600 dark:text-rose-300">
                  {error}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl border border-accent2/40 bg-accent2/15 px-4 py-2.5 text-sm font-semibold text-accent2 transition-colors hover:bg-accent2/25 disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </form>
          </div>

          <div className="glass-card p-6 shadow-xl">
            <div className="flex items-center gap-2 text-glass-text">
              <Link2 className="h-5 w-5 text-cyan-500 dark:text-cyan-400" />
              <h2 className="text-lg font-semibold">Email sign-in link</h2>
            </div>
            <p className="mt-2 text-xs text-glass-text-2 leading-relaxed">
              For{" "}
              <strong className="text-glass-text">
                existing accounts only
              </strong>
              : we&apos;ll send a one-time link. Open it to finish signing in on
              this site—no password needed for that session.
            </p>
            <form
              onSubmit={handleMagicLink}
              className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-stretch"
            >
              <div className="relative flex-1">
                <Mail className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-glass-text-3" />
                <input
                  type="email"
                  value={magicEmail}
                  onChange={(ev) => setMagicEmail(ev.target.value)}
                  placeholder="you@example.com"
                  disabled={magicStatus === "sending"}
                  className="glass-input w-full py-2.5 pl-10 pr-3 text-sm disabled:opacity-60"
                />
              </div>
              <button
                type="submit"
                disabled={magicStatus === "sending"}
                className="shrink-0 rounded-xl border border-glass bg-glass-2 px-4 py-2.5 text-sm font-medium text-glass-text transition-colors hover:bg-glass-3 disabled:opacity-60"
              >
                {magicStatus === "sending" ? "Sending…" : "Send link"}
              </button>
            </form>
            {magicStatus === "sent" && magicMessage ? (
              <p className="mt-3 text-sm text-emerald-600 dark:text-emerald-300">
                {magicMessage}
              </p>
            ) : null}
            {magicStatus === "error" && magicMessage ? (
              <p className="mt-3 text-sm text-rose-600 dark:text-rose-200">
                {magicMessage}
              </p>
            ) : null}
          </div>
        </div>

        <footer className="text-center text-sm text-glass-text-3">
          © {new Date().getFullYear()} QueueMart 1260221-X All rights reserved.
          ·{" "}
          <Link to="/" className="text-accent2 hover:underline">
            Home
          </Link>
        </footer>
      </div>
    </div>
  );
}
